import { useRef, useState } from 'react';
import styled from 'styled-components';
import Flex from 'components/ui/Flex';
import SearchInput from 'components/Search/SearchInput';
import Text from 'components/ui/Text';
import { useTranslation } from 'react-i18next';
import { ContentContainer } from 'components/ui/ContentContainer';
import { orderThunks, orderSelectors } from 'store/reducers/orderSlice';
import { clearReturnSlip as clearReturnSlipSet } from 'store/reducers/returnSlice';
import { toast } from 'react-toastify';
import useAppSelector from 'hooks/useAppSelector';
import useAppDispatch from 'hooks/useAppDispatch';
import { useNavigate } from 'react-router-dom';
import { setCurrentView } from 'store/reducers/navigationSlice';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBarcode } from '@fortawesome/pro-light-svg-icons';
import { getStatusTypeString } from 'utils/helpers';

const SearchBarcode = () => {
    const { t } = useTranslation();
    const inputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [barcodeValue, setBarcodeValue] = useState('');
    const orderLoading = useAppSelector(orderSelectors.selectOrderLoading);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const handleScan = async (value: string) => {
        try {
            // Clear any existing return slip before searching
            dispatch(clearReturnSlipSet());

            const result = await dispatch(orderThunks.getOrderByBarcode(value)).unwrap();
            if (result) {
                if (result.type === 'return' && result.returnId != null) {
                    const statusKey = getStatusTypeString(result.returnStatus ?? undefined);
                    navigate(`/returns/${statusKey}/${result.returnId}`, {
                        state: { origin: statusKey, returnStatus: result.returnStatus },
                    });
                } else if (result.type === 'order' && result.orderNumber) {
                    dispatch(setCurrentView('searchOrders'));
                    navigate(`/search/order/${result.orderNumber}`, {
                        state: {
                            orderIdFromPath: result.orderNumber,
                            fillMaxReturnQty: false,
                        },
                    });
                }
            }
        } catch (error) {
            toast.error(t('orderNotFoundForBarcode', 'No order found for this barcode'), {
                autoClose: 3500,
                closeOnClick: true,
            });
        }
    };

    const handleSearch = () => {
        if (inputRef.current && !orderLoading) {
            handleScan(inputRef.current.value);
        }
    };

    return (
        <ContentContainer
            $grow={1}
            column
            className="scan-bar-container pb-3"
            align="center"
            justify="center"
            gap="1rem"
        >
            <Title lineheight={20} fontSize={32} as="h2">
                {t('scanBarcodes', 'Scan Barcode')}
            </Title>
            <ScanBar $grow={1} align="center" justify="center" className="scan-bar">
                <StyledInput
                    placeholder={t('scanOrEnterBarcode', 'Scan or enter barcode...')}
                    inputRef={inputRef}
                    containerRef={containerRef}
                    searchString={barcodeValue}
                    onSearch={(e) => setBarcodeValue(e.target.value)}
                    onClear={() => {
                        setBarcodeValue('');
                        if (inputRef.current) {
                            inputRef.current.value = '';
                            inputRef.current.focus();
                        }
                    }}
                    onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
                        if (e.key === 'Enter') {
                            handleSearch();
                        }
                    }}
                    onPaste={(e: React.ClipboardEvent<HTMLInputElement>) => {
                        const pasted = e.clipboardData.getData('Text');
                        if (pasted.length >= 8) {
                            setBarcodeValue(pasted);
                            setTimeout(() => handleScan(pasted), 0);
                        }
                    }}
                    onIconClick={() => {
                        if (inputRef.current && !orderLoading) {
                            handleScan(inputRef.current.value);
                        }
                    }}
                />
            </ScanBar>
            <FontAwesomeIcon icon={faBarcode} className="mt-3" size="3x" />
            <Description>
                {t('scanOrEnterBarcodeDescription', 'Scan a barcode or enter it manually to search for an item.')}
            </Description>
        </ContentContainer>
    );
};

export default SearchBarcode;

const ScanBar = styled(Flex)`
    width: 100%;
`;

const StyledInput = styled(SearchInput)`
    width: 100%;
`;

const Title = styled(Text)`
    align-self: flex-start;
    margin-bottom: 0;
`;

const Description = styled.p`
    margin-top: 0;
    color: #555;
    font-size: 1rem;
`;
